import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Banknote, CreditCard, DollarSign, Eye, EyeOff, Loader2 } from "lucide-react";

interface PaymentMethod {
  id: string;
  method: string;
  is_active: boolean;
  environment: string;
  client_id: string | null;
  secret_key: string | null;
}

const methodConfig: Record<string, { label: string; icon: typeof CreditCard; clientLabel: string; secretLabel: string }> = {
  cash: { label: "Cash", icon: Banknote, clientLabel: "", secretLabel: "" },
  paypal: { label: "PayPal", icon: DollarSign, clientLabel: "Client ID", secretLabel: "Secret Key" },
  stripe: { label: "Stripe", icon: CreditCard, clientLabel: "Publishable Key", secretLabel: "Secret Key" },
};

export function PaymentMethodsSection() {
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  const fetchMethods = async () => {
    const { data, error } = await supabase
      .from("payment_methods")
      .select("*")
      .order("method");
    if (error) {
      toast.error("Failed to load payment methods");
    } else {
      setMethods((data as any) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchMethods(); }, []);

  const updateField = (id: string, field: keyof PaymentMethod, value: any) => {
    setMethods((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)));
  };

  const handleSave = async (pm: PaymentMethod) => {
    setSaving(pm.id);
    const { error } = await supabase
      .from("payment_methods")
      .update({
        is_active: pm.is_active,
        environment: pm.environment,
        client_id: pm.client_id,
        secret_key: pm.secret_key,
      } as any)
      .eq("id", pm.id);
    setSaving(null);
    if (error) {
      toast.error("Failed to save");
    } else {
      toast.success(`${methodConfig[pm.method]?.label ?? pm.method} saved`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {methods.map((pm) => {
        const cfg = methodConfig[pm.method] ?? { label: pm.method, icon: CreditCard, clientLabel: "Client ID", secretLabel: "Secret Key" };
        const Icon = cfg.icon;
        const isCash = pm.method === "cash";
        const showSecret = showSecrets[pm.id] ?? false;

        return (
          <Card key={pm.id} className="flex flex-col">
            <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-4">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-lg">{cfg.label}</CardTitle>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{pm.is_active ? "Active" : "Inactive"}</span>
                <Switch checked={pm.is_active} onCheckedChange={(v) => updateField(pm.id, "is_active", v)} />
              </div>
            </CardHeader>

            <CardContent className="flex-1 space-y-4">
              {!isCash && (
                <>
                  <div className="space-y-2">
                    <Label>Environment</Label>
                    <Select value={pm.environment} onValueChange={(v) => updateField(pm.id, "environment", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{cfg.clientLabel}</Label>
                    <Input
                      placeholder={`Enter ${cfg.clientLabel}`}
                      value={pm.client_id ?? ""}
                      onChange={(e) => updateField(pm.id, "client_id", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{cfg.secretLabel}</Label>
                    <div className="relative">
                      <Input
                        type={showSecret ? "text" : "password"}
                        placeholder={`Enter ${cfg.secretLabel}`}
                        value={pm.secret_key ?? ""}
                        onChange={(e) => updateField(pm.id, "secret_key", e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-10 w-10"
                        onClick={() => setShowSecrets((s) => ({ ...s, [pm.id]: !showSecret }))}
                      >
                        {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {isCash && (
                <p className="text-sm text-muted-foreground">No credentials needed for cash payments.</p>
              )}

              <Button className="w-full" onClick={() => handleSave(pm)} disabled={saving === pm.id}>
                {saving === pm.id && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
